<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland size="lg">
    <form class="four-template-edit" @submit.prevent="handleSave">
      <!-- Основная секция: большое изображение слева и текст справа -->
      <div class="four-template-edit__main-section">
        <div class="four-template-edit__main-image">
          <div class="four-template-edit__main-image--relative">
            <TemplateMediaSlot
              :model-value="images[0]"
              @update:model-value="(urls) => handleUpdateImage(0, urls)"
            />
            <div class="four-template-edit__main-subimage">
              <TemplateMediaSlot
                :model-value="images[1]"
                @update:model-value="(urls) => handleUpdateImage(1, urls)"
              />
            </div>
          </div>
        </div>

        <div class="four-template-edit__main-text">
          <BaseForm
            ref="formRef1"
            v-model="formModel"
            :schema="fourTemplateHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.mainText"
            @add="handleStartAddButton('mainText')"
            @edit="handleOpenModuleSelect('mainText')"
            @clear="handleClearButton('mainText')"
          />
        </div>
      </div>

      <div class="four-template-edit__second-header">
        <BaseForm
          ref="formRef6"
          v-model="formModel"
          :schema="fourTemplateOneSecondHeaderSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.secondHeader"
          @add="handleStartAddButton('secondHeader')"
          @edit="handleOpenModuleSelect('secondHeader')"
          @clear="handleClearButton('secondHeader')"
        />
      </div>

      <!-- Три блока: изображение + текст под каждым -->
      <div class="four-template-edit__triple-sections">
        <div class="four-template-edit__section-item">
          <div class="four-template-edit__section-image">
            <TemplateMediaSlot
              :model-value="images[2]"
              @update:model-value="(urls) => handleUpdateImage(2, urls)"
            />
          </div>
          <div class="four-template-edit__section-text">
            <BaseForm
              ref="formRef2"
              v-model="formModel"
              :schema="fourTemplateFirstHeaderSchema"
              :with-form-wrapper="false"
              :with-error-message="false"
            />
            <TemplateLandingButtonSlot
              full-width
              :button="buttons.firstSection"
              @add="handleStartAddButton('firstSection')"
              @edit="handleOpenModuleSelect('firstSection')"
              @clear="handleClearButton('firstSection')"
            />
          </div>
        </div>
        <div class="four-template-edit__section-item">
          <div class="four-template-edit__section-image">
            <TemplateMediaSlot
              :model-value="images[3]"
              @update:model-value="(urls) => handleUpdateImage(3, urls)"
            />
          </div>
          <div class="four-template-edit__section-text">
            <BaseForm
              ref="formRef3"
              v-model="formModel"
              :schema="fourTemplateSecondHeaderSchema"
              :with-form-wrapper="false"
              :with-error-message="false"
            />
            <TemplateLandingButtonSlot
              full-width
              :button="buttons.secondSection"
              @add="handleStartAddButton('secondSection')"
              @edit="handleOpenModuleSelect('secondSection')"
              @clear="handleClearButton('secondSection')"
            />
          </div>
        </div>
        <div class="four-template-edit__section-item">
          <div class="four-template-edit__section-image">
            <TemplateMediaSlot
              :model-value="images[4]"
              @update:model-value="(urls) => handleUpdateImage(4, urls)"
            />
          </div>
          <div class="four-template-edit__section-text">
            <BaseForm
              ref="formRef4"
              v-model="formModel"
              :schema="fourTemplateThirdHeaderSchema"
              :with-form-wrapper="false"
              :with-error-message="false"
            />
            <TemplateLandingButtonSlot
              full-width
              :button="buttons.thirdSection"
              @add="handleStartAddButton('thirdSection')"
              @edit="handleOpenModuleSelect('thirdSection')"
              @clear="handleClearButton('thirdSection')"
            />
          </div>
        </div>
      </div>

      <!-- Дополнительный блок с заголовком и описанием -->
      <div class="four-template-edit__additional-section">
        <BaseForm
          ref="formRef5"
          v-model="formModel"
          :schema="fourTemplateAdditionalSectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.additional"
          @add="handleStartAddButton('additional')"
          @edit="handleOpenModuleSelect('additional')"
          @clear="handleClearButton('additional')"
        />
      </div>

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>

  <LandingButtonTypePopup
    v-model="isButtonTypePopupOpen"
    @select-module="handleChooseButtonType('module')"
    @select-object="handleChooseButtonType('moduleObject')"
  />
</template>

<script setup lang="ts">
import type { IFourFormData, IFourTemplateData } from '~/entities/projects/model/templates-interface'
import type { IFormExpose } from '~/shared/ui'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTemplateEditProjectOrRequestMeta } from '~/entities/projects'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { validateTemplateImages } from '~/entities/projects/model/templateImageValidation'
import {
  fourTemplateAdditionalSectionSchema,
  fourTemplateFirstHeaderSchema,
  fourTemplateHeaderSchema,
  fourTemplateOneSecondHeaderSchema,
  fourTemplateSecondHeaderSchema,
  fourTemplateThirdHeaderSchema,
} from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useTemplateEditButtons } from '~/entities/projects/model/useTemplateEditButtons'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import LandingButtonTypePopup from '~/entities/projects/ui/templates/LandingButtonTypePopup.vue'
import TemplateLandingButtonSlot from '~/entities/projects/ui/templates/TemplateLandingButtonSlot.vue'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { permissions, useLoading } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseIsland,
  BaseNavigationBar,
} from '~/shared/ui'

const router = useRouter()
const route = useRoute()

const { uploadFromBlobUrl } = uploadProjectImage()
const { isProjectRequestContext, resolvedProjectTitle, projectId, routeParamForLinks } = useTemplateEditProjectOrRequestMeta()
const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)

const images = ref<string[]>([])
const loading = useLoading()

const formModel = ref<IFourFormData>({
  title: '',
  description: '',
  secondHeaderTitle: '',
  secondHeaderDescription: '',
  firstSectionTitle: '',
  firstSectionDescription: '',
  secondSectionTitle: '',
  secondSectionDescription: '',
  thirdSectionTitle: '',
  thirdSectionDescription: '',
  bottomSectionTitle: '',
  bottomSectionDescription: '',
})

const {
  buttons,
  isButtonTypePopupOpen,
  loadButtonsFromContent,
  handleStartAddButton,
  handleOpenModuleSelect,
  handleChooseButtonType,
  handleClearButton,
  getButtonsForSave,
  clearDraft,
} = useTemplateEditButtons({
  templateNumericId: templateId.four_template,
  editRoutePermission: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.FOUR_TEMPLATE.EDIT.PERMISSION,
  getDraftPayload: () => ({
    formModel: { ...formModel.value },
    images: [...images.value],
    buttons: { ...buttons.value },
  }),
  applyDraftPayload: (draft) => {
    formModel.value = draft.formModel as IFourFormData
    images.value = [...draft.images]
    buttons.value = { ...draft.buttons }
  },
})

const formRef1 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef2 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef3 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef4 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef5 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef6 = ref<ComponentPublicInstance<IFormExpose>>()

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as unknown as IFourTemplateData

    if (content.id === templateId.four_template) {
      formModel.value = {
        title: content.title || resolvedProjectTitle.value,
        description: content.description || '',
        secondHeaderTitle: content.secondHeaderTitle || '',
        secondHeaderDescription: content.secondHeaderDescription || '',
        firstSectionTitle: content.firstSectionTitle || '',
        firstSectionDescription: content.firstSectionDescription || '',
        secondSectionTitle: content.secondSectionTitle || '',
        secondSectionDescription: content.secondSectionDescription || '',
        thirdSectionTitle: content.thirdSectionTitle || '',
        thirdSectionDescription: content.thirdSectionDescription || '',
        bottomSectionTitle: content.bottomSectionTitle || '',
        bottomSectionDescription: content.bottomSectionDescription || '',
      }

      if (content.images) {
        images.value = [
          content.images.mainImage1 || '',
          content.images.mainImage2 || '',
          content.images.sectionImage1 || '',
          content.images.sectionImage2 || '',
          content.images.sectionImage3 || '',
        ]
      }

      loadButtonsFromContent(content.buttons)
    }
  }
}, { immediate: true })

watch(
  resolvedProjectTitle,
  (name) => {
    if (name && !formModel.value.title) {
      formModel.value.title = name
    }
  },
  { immediate: true },
)

function handleUpdateImage(index: number, urls: string[]) {
  const url = urls[0] ?? ''
  const next = [...images.value]
  while (next.length <= index) {
    next.push('')
  }
  next[index] = url
  images.value = next
}

async function handleSave() {
  const validationResults = await Promise.all([
    formRef1.value?.validate(),
    formRef6.value?.validate(),
    formRef2.value?.validate(),
    formRef3.value?.validate(),
    formRef4.value?.validate(),
    formRef5.value?.validate(),
  ])

  const allValid = validationResults.every(result => result?.valid)

  if (!allValid) {
    return
  }

  const uploadPromises = images.value.map(url => url ? uploadFromBlobUrl(url) : Promise.resolve(null))
  const uploadedUrls = await Promise.all(uploadPromises)

  const finalImages = images.value.map((image, index) =>
    uploadedUrls[index] || image,
  )

  if (!validateTemplateImages({
    requiredImageCount: 5,
    images: finalImages,
  })) {
    return
  }
  await loading.wrapLoading(async () => {
    const templateContent = {
      id: templateId.four_template,
      title: resolvedProjectTitle.value,
      description: formModel.value.description,
      secondHeaderTitle: formModel.value.secondHeaderTitle,
      secondHeaderDescription: formModel.value.secondHeaderDescription,
      firstSectionTitle: formModel.value.firstSectionTitle,
      firstSectionDescription: formModel.value.firstSectionDescription,
      secondSectionTitle: formModel.value.secondSectionTitle,
      secondSectionDescription: formModel.value.secondSectionDescription,
      thirdSectionTitle: formModel.value.thirdSectionTitle,
      thirdSectionDescription: formModel.value.thirdSectionDescription,
      bottomSectionTitle: formModel.value.bottomSectionTitle,
      bottomSectionDescription: formModel.value.bottomSectionDescription,
      buttons: getButtonsForSave(),
      images: {
        mainImage1: finalImages[0],
        mainImage2: finalImages[1],
        sectionImage1: finalImages[2],
        sectionImage2: finalImages[3],
        sectionImage3: finalImages[4],
      },
    }

    await saveTemplate(templateContent as IFourTemplateData)
    clearDraft()

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.four-template-edit {
  display: grid;
  gap: rem(48px);
  grid-template-columns: 1fr;

  &__header,
  &__second-header {
    display: grid;
    gap: rem(16px);
  }

  &__main-section {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__main-image {
    display: grid;
    width: fit-content;

    :deep(.template-media-slot) {
      width: 456px;

      @include bp-media('md') {
        width: 377px;
      }

      @include bp-media('xs', 'sm') {
        width: 274px;
      }
    }

    &--relative {
      width: 560px;
      position: relative;

      @include bp-media('xs', 'sm') {
        width: fit-content;
      }
    }
  }

  &__main-subimage {
    position: absolute;
    bottom: rem(20px);
    right: rem(0px);

    :deep(.template-media-slot) {
      width: 240px;
    }

    @include bp-media('md') {
      right: rem(80px);

      :deep(.template-media-slot) {
        width: 189px;
      }
    }

    @include bp-media('xs', 'sm') {
      bottom: rem(20px);
      right: rem(-20px);

      :deep(.template-media-slot) {
        width: 120px;
      }
    }
  }

  &__main-text {
    display: flex;
    flex-direction: column;
    gap: rem(16px);
    padding-top: rem(96px);

    @include bp-media('xs', 'sm', 'md', 'lg') {
      padding-top: 0;
    }
  }

  &__triple-sections {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    :deep(.template-media-slot) {
      @include bp-media('sm', 'md', 'lg') {
        width: 214px;
      }
    }

    @include  bp-media('md', 'lg') {
      gap: rem(10px);
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__section-item {
    display: flex;
    flex-direction: column;
    gap: rem(16px);

    @include bp-media('md', 'lg') {
      max-width: 214px;
    }
  }

  &__section-text {
    display: flex;
    flex-direction: column;
    gap: rem(8px);

    @include bp-media('md', 'lg') {
      :deep(.base-input) {
        max-width: 214px;
      }

      :deep(.base-textarea) {
        max-width: 214px;
      }
    }
  }

  &__main-image,
  &__side-image {
    display: grid;
  }

  &__quadruple-headers {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-item {
    display: grid;
    gap: rem(16px);
  }

  &__text-image-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);
  }

}
</style>
